import { Link, useNavigate } from "react-router";
import type { Me } from "@atrium/shared";
import { cn } from "../../lib/cn";
import { PresenceAvatar, StatusLine } from "../../components/PresenceIndicator";
import { StatusPicker } from "../../components/StatusPicker";
import { IconButton } from "../../components/ui/Button";
import { Spinner } from "../../components/ui/Spinner";
import { IconDeafened, IconHangUp, IconHeadphones, IconMic, IconMicOff, IconSettings, IconSignal } from "../../components/icons";
import { useStructure } from "../communities/hooks";
import { useVoice } from "./VoiceProvider";
import { ShareButton, useShareBlockedReason } from "./VoiceControls";

/**
 * Bottom of the channel sidebar: live voice connection plus your own controls.
 * On desktop the rail already carries your avatar and account menu, so the
 * identity row is mobile-only and mute/deafen move into the voice row.
 */
export function UserPanel({ me }: { me: Me }) {
  const voice = useVoice();
  const navigate = useNavigate();
  const inVoice = voice.status !== "idle";
  const { data: structure } = useStructure(voice.communityId ?? undefined);
  const channel = structure?.channels.find((c) => c.id === voice.channelId) ?? null;
  const shareReason = useShareBlockedReason(channel, structure);
  const audience = voice.room?.kind === "stage" && !voice.self?.speaker;
  const where = voice.room?.kind === "call" ? `/messages/${voice.callId}` : `/c/${voice.communityId}/${voice.channelId}`;
  const connected = voice.room?.kind === "call" ? "Call connected" : voice.room?.kind === "stage" ? "Stage connected" : "Voice connected";
  return (
    <div className={cn("sidebar-surface shrink-0 border-t border-line", !inVoice && "md:hidden")}>
      {inVoice && (
        <div className="flex items-center gap-1 border-b border-line px-3 py-2 md:border-b-0">
          <IconSignal className={cn("icon size-[18px]", voice.status === "connected" ? "text-success" : "text-warning")} aria-hidden />
          <div className="ml-1 min-w-0 flex-1">
            <div className={cn("text-sm font-semibold", voice.status === "connected" ? "text-success" : "text-warning")}>
              {voice.status === "connected" ? connected : "Connecting…"}
            </div>
            <Link to={where} className="block truncate text-xs text-fg-3 hover:text-fg-2 hover:underline">
              {voice.channelName}
            </Link>
          </div>
          {voice.status === "connecting" && <Spinner className="size-4 text-fg-3" />}
          {voice.status === "connected" && !audience && <ShareButton size="sm" reason={shareReason} />}
          <span className="hidden md:contents">
            <IconButton size="sm" label={voice.muted ? "Unmute" : "Mute"} icon={voice.muted ? IconMicOff : IconMic} active={voice.muted} onClick={voice.toggleMute} className={cn(voice.muted && "text-danger")} />
            <IconButton size="sm" label={voice.deafened ? "Undeafen" : "Deafen"} icon={voice.deafened ? IconDeafened : IconHeadphones} active={voice.deafened} onClick={voice.toggleDeafen} className={cn(voice.deafened && "text-danger")} />
          </span>
          <IconButton label="Disconnect" icon={IconHangUp} tone="danger" size="sm" onClick={voice.leave} />
        </div>
      )}
      <div className="flex h-14 items-center gap-2 px-2 md:hidden">
        <StatusPicker>
          <button aria-label="Set your status" className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-1 py-1 text-left hover:bg-surface">
            <PresenceAvatar userId={me.id} src={me.avatarUrl} name={me.displayName} size="md" />
            <div className="min-w-0 leading-tight">
              <div className="username truncate text-sm text-fg">{me.displayName}</div>
              <StatusLine userId={me.id} fallback={`@${me.username}`} />
            </div>
          </button>
        </StatusPicker>
        <IconButton size="sm" label={voice.muted ? "Unmute" : "Mute"} icon={voice.muted ? IconMicOff : IconMic} active={voice.muted} onClick={voice.toggleMute} className={cn(voice.muted && "text-danger")} />
        <IconButton size="sm" label={voice.deafened ? "Undeafen" : "Deafen"} icon={voice.deafened ? IconDeafened : IconHeadphones} active={voice.deafened} onClick={voice.toggleDeafen} className={cn(voice.deafened && "text-danger")} />
        <IconButton size="sm" label="Settings" icon={IconSettings} onClick={() => navigate("/settings")} />
      </div>
    </div>
  );
}
